/*eslint-disable camelcase*/
import { path } from 'ramda';

import { getWindowObj, electronOnly } from '@ringcentral/rwc-utils/src/client-detection';
import { API_KEY, APIS } from '@ringcentral/rwc-utils/src/desktop-tools/declarations';
import { UnknownFunction } from '../types';
import { NativeVideoprocessingAPICreator } from './desktop-api-types';

export type FeatureFlagsGetter = (flagname: string) => {
    enabled: boolean;
    [key: string]: any;
};

export const getElectronApiName = (apiKey: string): string => APIS[apiKey]?.name;

export const getElectronApi = <Type>(apiKey: string): Type | UnknownFunction | undefined =>
    path(APIS[apiKey]?.path, getWindowObj());

/**
 * Unsafe getters are unsafe in terms of high probability of undefined
 * behavior. Safe getters will throw to explicitly indicate potential
 * coding mistake on early stages of development.
 */

/**
 * Unsafe getters for custom Electron API's
 */
const unsafe_getCaptureSourcePickerAPI = () => getElectronApi(API_KEY.CAPTURE_SOURCE_PICKER);
const unsafe_getScreenRecordingAccessStatusAPI = () =>
    getElectronApi(API_KEY.CHECK_SS_ACCESS_STATUS);
const unsafe_getBordersHaloAPI = () => getElectronApi(API_KEY.BORDERS_HALO);
const unsafe_getSharingBarAPI = () => getElectronApi(API_KEY.SHARING_BAR);
const unsafe_getAppPidAPI = () => getElectronApi(API_KEY.SET_APP_PID);
const unsafe_getAnnotationsPainterAPI = () => getElectronApi(API_KEY.ANNOTATIONS_PAINTER);
const unsafe_getAnnotationsTrackerAPI = () => getElectronApi(API_KEY.ANNOTATIONS_TRACKER);
const unsafe_getRemoteControlAPI = () => getElectronApi(API_KEY.REMOTE_CONTROL);
const unsafe_getOptimizeForVideoAPI = () => getElectronApi(API_KEY.SET_OPTIMIZE_FOR_VIDEO);
const unsafe_getVideoEffectsAPI = () =>
    getElectronApi<NativeVideoprocessingAPICreator>(API_KEY.VIDEO_EFFECTS);
const unsafe_getVideoEffectsAPIV2 = () =>
    getElectronApi<NativeVideoprocessingAPICreator>(API_KEY.VIDEO_EFFECTS_2);
const unsafe_getShareMediaAPI = () => getElectronApi(API_KEY.SHARE_MEDIA);
const unsafe_getRendererKIT = () => getElectronApi(API_KEY.GET_RENDERER_KIT);
const unsafe_getSetOptimizeForMultipleMonitorsAPI = () =>
    getElectronApi(API_KEY.SET_OPTIMIZE_FOR_MULTIPLE_MONITORS);
const unsafe_getSetForceCapturer = () => getElectronApi(API_KEY.SET_FORCE_CAPTURER);

/**
 * Unsafe getters for standard Electron API's
 */
const unsafe_getIPCRendererAPI = () => getElectronApi(API_KEY.IPC_RENDERER);
const unsafe_getDesktopCapturerAPI = () => getElectronApi(API_KEY.DESKTOP_CAPTURER);
const unsafe_getSystemPreferencesAPI = () => getElectronApi(API_KEY.SYSTEM_PREFERENCES);
const unsafe_getAnalytics = () => getElectronApi(API_KEY.ANALYTICS);
const unsafe_getDesktopLogAPI = () => getElectronApi<(msg: string) => void>(API_KEY.DESKTOP_LOG);
const unsafe_getRCDesktopCapturerController = () =>
    getElectronApi(API_KEY.DESKTOP_CAPTURER_CONTROLLER);
const unsafe_getLaunchDarklyAPI = () =>
    getElectronApi<(flags: Record<string, any>) => void>(API_KEY.SET_LAUNCH_DARKLY_FLAGS);
const unsafe_getSetFeatureFlagGetter = () =>
    getElectronApi<(getFlag: FeatureFlagsGetter) => void>(API_KEY.SET_FEATURE_FLAG_GETTER);
const unsafe_getDataForExternalCapturerAPI = () =>
    getElectronApi(API_KEY.FORM_RC_DATA_FOR_EXTERNAL_CAPTURER);

/**
 * Safe getters for custom Electron API's
 */
export const getCaptureSourcePickerAPI = electronOnly(unsafe_getCaptureSourcePickerAPI);
export const getScreenRecordingAccessStatusAPI = electronOnly(
    unsafe_getScreenRecordingAccessStatusAPI
);
export const getBordersHaloAPI = electronOnly(unsafe_getBordersHaloAPI);
export const getSharingBarAPI = electronOnly(unsafe_getSharingBarAPI);
export const getAppPidAPI = electronOnly(unsafe_getAppPidAPI);
export const getAnnotationsPainterAPI = electronOnly(unsafe_getAnnotationsPainterAPI);
export const getAnnotationsTrackerAPI = electronOnly(unsafe_getAnnotationsTrackerAPI);
export const getRemoteControlAPI = electronOnly(unsafe_getRemoteControlAPI);
export const getOptimizeForVideoAPI = electronOnly(unsafe_getOptimizeForVideoAPI);
export const getVideoEffectsAPI = electronOnly(unsafe_getVideoEffectsAPI);
export const getVideoEffectsAPIV2 = electronOnly(unsafe_getVideoEffectsAPIV2);
export const getShareMediaAPI = electronOnly(unsafe_getShareMediaAPI);
export const getGetRendererKITAPI = electronOnly(unsafe_getRendererKIT);
export const getSetOptimizeForMultipleMonitors = electronOnly(
    unsafe_getSetOptimizeForMultipleMonitorsAPI
);
export const getSetForceCapturer = electronOnly(unsafe_getSetForceCapturer);

/**
 * Standard Electron API's
 */
export const getIPCRendererAPI = electronOnly(unsafe_getIPCRendererAPI);
export const getDesktopCapturerAPI = electronOnly(unsafe_getDesktopCapturerAPI);
export const getSystemPreferencesAPI = electronOnly(unsafe_getSystemPreferencesAPI);
export const getAnalytics = electronOnly(unsafe_getAnalytics);
export const getDesktopLogAPI = electronOnly(unsafe_getDesktopLogAPI);
export const getRCDesktopCapturerController = electronOnly(unsafe_getRCDesktopCapturerController);
export const getDesktopLaunchDarklyAPI = electronOnly(unsafe_getLaunchDarklyAPI);
export const getSetFeatureFlagGetter = electronOnly(unsafe_getSetFeatureFlagGetter);
export const getDataForExternalCapturerAPI = electronOnly(unsafe_getDataForExternalCapturerAPI);
